import { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatApiErrorDetail } from "@/lib/api";
import { Loader2, Search, Star, ShieldCheck, Clock, MapPin, Phone, Mail, Plus } from "lucide-react";
import PublicAIChatWidget from "@/components/public/PublicAIChatWidget";
import { ForgotPasswordDialog } from "@/components/app/ForgotPasswordDialog";

const LOGO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_complaint-tracker-102/artifacts/ljjl5i6a_7232BE5C-9BE6-40DB-AD36-2C018C990278.png";
const HERO_URL =
  "https://customer-assets-lqy194kg.emergentagent.net/job_complaint-tracker-102/artifacts/nanbffsr_images-2.jpeg";

const REVIEWS = [
  {
    name: "Carla M.",
    city: "Madrid",
    text: "Puntualidad y trato exquisito. El conductor nos esperaba con cartel y ayudó con las maletas. Repetiremos sin duda.",
  },
  {
    name: "Luis Fernández",
    city: "Barcelona",
    text: "Traslado al aeropuerto impecable. Coche limpio, agua fría y trayecto muy cómodo. Recomendadísimo.",
  },
  {
    name: "María & Diego",
    city: "Valencia",
    text: "Reservamos una minivan para 7 personas. Todo perfecto: comunicación por WhatsApp, precio cerrado y sin sorpresas.",
  },
  {
    name: "Sofía R.",
    city: "Sevilla",
    text: "Muy profesionales. Servicio VIP a un precio muy razonable. Definitivamente los mejores traslados de Madrid.",
  },
  {
    name: "Alessandro P.",
    city: "Roma",
    text: "Best transfer service in Madrid! The driver was on time, very kind and drove smoothly. 100% recommended.",
  },
  {
    name: "Jean-Paul D.",
    city: "París",
    text: "Servicio irreprochable. Nos recibieron en la terminal y nos llevaron al hotel sin ningún problema. Gracias.",
  },
];

function Stars({ n = 5 }) {
  return (
    <div className="flex gap-0.5" aria-label={`${n} de 5 estrellas`}>
      {Array.from({ length: n }).map((_, i) => (
        <Star key={i} size={14} className="fill-[#F5B301] text-[#F5B301]" />
      ))}
    </div>
  );
}

export default function LoginPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  if (user && user !== false) {
    return <Navigate to="/" replace />;
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/", { replace: true });
    } catch (e2) {
      setErr(formatApiErrorDetail(e2?.response?.data?.detail) || e2.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="login-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <img
          src={HERO_URL}
          alt="Madrid al atardecer"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/80" />

        {/* Top bar */}
        <header className="relative z-10 max-w-7xl mx-auto flex items-center justify-between px-6 sm:px-10 py-5">
          <div className="flex items-center gap-3">
            <img
              src={LOGO_URL}
              alt="DS Travel Tours"
              className="w-12 h-12 object-contain drop-shadow-[0_2px_10px_rgba(255,255,255,0.35)]"
            />
            <div className="text-white">
              <div className="font-display text-lg font-bold tracking-tight leading-none">
                DS TRAVEL TOURS
              </div>
              <div className="text-[10px] uppercase tracking-[0.3em] text-white/70">
                Madrid · Traslados premium
              </div>
            </div>
          </div>
          <a
            href="tel:+34624458803"
            className="hidden sm:inline-flex items-center gap-2 text-white text-sm font-semibold hover:opacity-80"
          >
            <Phone size={16} /> +34 624 458 803
          </a>
        </header>

        {/* Hero content + form */}
        <div className="relative z-10 max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-8 px-6 sm:px-10 pt-10 pb-24 lg:pt-24 lg:pb-40">
          <div className="lg:col-span-3 text-white animate-fade-up">
            <div className="inline-flex items-center gap-2 border border-white/40 bg-white/10 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-[0.3em] mb-6">
              <ShieldCheck size={12} /> Licencia VTC · Conductores verificados
            </div>
            <h1 className="font-display font-bold leading-[0.9] tracking-tight text-5xl sm:text-6xl lg:text-7xl mb-6">
              Bienvenidos a<br />
              <span className="text-[#F5B301]">DS Travel Tours</span>
            </h1>
            <p className="text-lg text-white/85 max-w-xl leading-relaxed">
              Traslados privados en Madrid con la comodidad de un chófer profesional.
              Puntualidad, discreción y elegancia en cada trayecto.
            </p>

            <div className="flex flex-wrap items-center gap-6 mt-8 text-white/80 text-sm">
              <div className="flex items-center gap-2">
                <Clock size={16} className="text-[#F5B301]" /> 24/7 disponibles
              </div>
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-[#F5B301]" /> Toda España
              </div>
              <div className="flex items-center gap-2">
                <Stars n={5} />
                <span>+500 reseñas</span>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-8">
              <Link
                to="/reservar"
                data-testid="book-now-link"
                className="inline-flex items-center gap-2 px-6 h-12 bg-[#F5B301] text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-white transition-colors"
              >
                <Plus size={14} /> Reservar ahora
              </Link>
              <Link
                to="/consulta"
                data-testid="client-lookup-link"
                className="inline-flex items-center gap-2 px-6 h-12 bg-white text-black text-xs uppercase tracking-[0.25em] font-bold hover:bg-[#F5B301] transition-colors"
              >
                <Search size={14} /> Consultar mi reserva
              </Link>
            </div>
          </div>

          {/* Login card */}
          <div className="lg:col-span-2 animate-fade-up">
            <form
              onSubmit={submit}
              data-testid="login-form"
              className="bg-white/95 backdrop-blur-md border-2 border-white p-7 shadow-2xl"
            >
              <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-1">
                Acceso interno
              </div>
              <div className="font-display text-2xl font-bold mb-5">
                Panel de personal y conductores
              </div>
              <div className="space-y-4">
                <div>
                  <Label htmlFor="email" className="text-[10px] uppercase tracking-widest">
                    Correo
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="mt-1.5 h-11 rounded-none border-black focus-visible:ring-1 focus-visible:ring-black"
                    placeholder="tu@dstraveltours.com"
                    data-testid="login-email-input"
                  />
                </div>
                <div>
                  <Label htmlFor="password" className="text-[10px] uppercase tracking-widest">
                    Contraseña
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="mt-1.5 h-11 rounded-none border-black focus-visible:ring-1 focus-visible:ring-black"
                    placeholder="••••••••"
                    data-testid="login-password-input"
                  />
                </div>
                {err && (
                  <div
                    className="text-sm text-red-700 bg-red-50 border border-red-300 px-3 py-2"
                    data-testid="login-error"
                  >
                    {err}
                  </div>
                )}
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full h-12 rounded-none btn-primary text-sm uppercase tracking-[0.25em]"
                  data-testid="login-submit-btn"
                >
                  {loading ? <Loader2 className="animate-spin" size={16} /> : "Entrar"}
                </Button>
              </div>
              <div className="mt-4 text-center">
                <ForgotPasswordDialog />
              </div>
              <div className="mt-4 text-[11px] text-zinc-500 text-center">
                Solo personal autorizado. ¿Eres cliente? Usa &ldquo;Consultar mi reserva&rdquo;.
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Value props */}
      <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16 grid grid-cols-1 md:grid-cols-3 gap-6">
        {[
          {
            icon: ShieldCheck,
            title: "Licencia VTC oficial",
            text: "Todos nuestros conductores están registrados en el Ministerio de Fomento y verificados por nuestro equipo.",
          },
          {
            icon: Clock,
            title: "Puntualidad garantizada",
            text: "Seguimiento del vuelo, 30 min de cortesía en el aeropuerto y avisos automáticos por WhatsApp.",
          },
          {
            icon: Star,
            title: "Servicio 5 estrellas",
            text: "Vehículos Mercedes limpios, agua fría, cargador USB y trato exquisito de principio a fin.",
          },
        ].map(({ icon: Icon, title, text }) => (
          <div
            key={title}
            className="border border-black p-6 hover:bg-black hover:text-white transition-colors group"
          >
            <Icon size={26} className="mb-4 text-[#F5B301]" />
            <div className="font-display text-xl font-bold mb-2">{title}</div>
            <p className="text-sm text-zinc-600 group-hover:text-white/80 leading-relaxed">
              {text}
            </p>
          </div>
        ))}
      </section>

      {/* Reviews 5/5 */}
      <section className="bg-zinc-50 border-y border-zinc-200 py-20">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-4 mb-12">
            <div>
              <div className="text-[11px] uppercase tracking-[0.3em] text-zinc-500 mb-2">
                Lo que dicen nuestros clientes
              </div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
                Excelencia en cada trayecto
              </h2>
            </div>
            <div className="flex items-center gap-3 border-2 border-black bg-white px-5 py-3">
              <div className="font-display text-4xl font-bold leading-none">5.0</div>
              <div>
                <Stars n={5} />
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1">
                  +500 reseñas
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {REVIEWS.map((r) => (
              <article
                key={r.name}
                className="bg-white border border-zinc-200 p-6 hover:border-black hover:shadow-lg transition-all"
                data-testid={`review-${r.name.replace(/\W+/g, "-").toLowerCase()}`}
              >
                <Stars n={5} />
                <p className="text-sm text-zinc-700 leading-relaxed mt-4 mb-6">&ldquo;{r.text}&rdquo;</p>
                <div className="border-t border-zinc-200 pt-4 flex items-center gap-3">
                  <div className="w-10 h-10 bg-black text-white flex items-center justify-center font-display font-bold">
                    {r.name[0]}
                  </div>
                  <div>
                    <div className="font-semibold text-sm">{r.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500">
                      {r.city}
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Contact / footer */}
      <section className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <div className="flex items-center gap-3 mb-6">
              <img src={LOGO_URL} alt="" className="w-14 h-14 object-contain" />
              <div>
                <div className="font-display text-xl font-bold">DS Travel Tours LLC</div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">
                  Traslados premium en Madrid
                </div>
              </div>
            </div>
            <p className="text-white/70 max-w-xl leading-relaxed text-sm">
              Compañía especializada en traslados privados desde y hacia el Aeropuerto Adolfo
              Suárez Madrid-Barajas, estaciones de tren y hoteles. Disponemos de varios tipos de vehículos,
              conductores licenciados y atención al cliente 24/7.
            </p>
          </div>
          <div className="space-y-4" data-testid="customer-support-card">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/60">
              Atención al cliente
            </div>
            <a
              href="tel:+34624458803"
              className="flex items-center gap-3 text-sm font-semibold hover:text-[#F5B301]"
              data-testid="support-phone"
            >
              <span className="w-9 h-9 border border-white/40 flex items-center justify-center">
                <Phone size={14} />
              </span>
              +34 624 458 803
            </a>
            <a
              href="mailto:info@dstraveltoursllc.com"
              className="flex items-center gap-3 text-sm font-semibold hover:text-[#F5B301] break-all"
              data-testid="support-email"
            >
              <span className="w-9 h-9 border border-white/40 flex items-center justify-center shrink-0">
                <Mail size={14} />
              </span>
              info@dstraveltoursllc.com
            </a>
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-6 sm:px-10 mt-12 pt-6 border-t border-white/10 text-[10px] uppercase tracking-[0.3em] text-white/40">
          © {new Date().getFullYear()} DS Travel Tours LLC · Todos los derechos reservados
        </div>
      </section>
      <PublicAIChatWidget />
    </div>
  );
}
